// =============================================================================
// domain/dashAdjustments.ts — the account adjustments the DASHBOARD counts. #41
// -----------------------------------------------------------------------------
// A prop account moves for reasons that are not trades: the balance you started
// with, a payout, a deposit, a withdrawal, a correction. Beta folds those into
// the dashboard's headline number and into the equity curve, and this client
// did not — so the two printed different totals for the same account and the
// same week, and a payout simply did not appear.
//
// ── 🚨 THE RULE IS NARROWER THAN "ADD THEM UP", IN THREE WAYS ──
//
//   1. FEES ARE OUT. Eval, reset and activation fees are money out of pocket,
//      not trading performance. Beta learned this the hard way: a logged $165
//      eval fee read the headline $165 low. `PROP_FEE_TYPES` is the list, and
//      `isBalanceMoving` is the predicate — shared with the balance builders so
//      the two cannot drift apart.
//
//   2. THE WINDOW APPLIES. An adjustment dated outside the active range is
//      dropped, exactly like a trade outside it. Otherwise last year's deposit
//      lands in this week's total.
//
//   3. THE STATISTICS DO NOT MOVE. This total is added to the DISPLAYED figure
//      and to the curve, and to nothing else. Expectancy, profit factor and win
//      rate stay trade-only — folding $2,500 of payouts into `totalPnl` would
//      make a losing week read as profitable in every gauge on the screen, which
//      is a far worse defect than the one this fixes.
//
// ── ⚠ SCOPED BY THE ACCOUNT SELECTION ONLY, WHICH IS BETA'S RULE AND IS ODD ──
//
// `adjustmentsForActive` filters on `getActiveAccountIds()` and does NOT consult
// `excludeFromMetrics`. So a payout logged on a SIM account moves the headline
// while none of that account's trades do. That is a real inconsistency, and it
// is REPRODUCED here rather than fixed: parity is the gate for this phase
// (REBUILD_PLAN §7, "relocate, do not redesign"), and a client that quietly
// disagrees with beta about a money figure is the thing the differential harness
// exists to catch. Worth raising as its own question — it is not an oversight.
//
// ⚠ Dollars only. Cash does not convert to points, ticks or R, so callers in any
// other unit must pass an empty range or ignore the total — see the `unit` guard
// at the call site.
// =============================================================================

import { isBalanceMoving } from './account';

/**
 * ⚠ DECLARED HERE RATHER THAN IMPORTED, because THERE ARE TWO ADJUSTMENT TYPES
 * in this domain and neither one fits.
 *
 *   `account.ts`'s `Adjustment`      — narrow: no `note`, no `accountId`
 *   `types.ts`'s `AccountAdjustment` — wide: required `id`, `accountId`, `note`
 *
 * The loader attaches the WIDE one; `isBalanceMoving` takes the NARROW one.
 * Importing either would either lose the note (which the curve's hover needs)
 * or demand ids from every test fixture that has no reason to carry them. This
 * states exactly what is read and structurally accepts both.
 */
export interface DashAdjustmentInput {
  date: string | null;
  type: string;
  amount: number | null;
  note?: string | null;
}

/** Just enough of an account to find its adjustments. */
export interface DashAdjustmentAccount {
  id: string;
  adjustments?: readonly DashAdjustmentInput[] | null;
}

/** The active window. Either end may be open. */
export interface DashRange {
  startDate: string | null;
  endDate: string | null;
}

/**
 * One adjustment, flattened for the curve.
 *
 * `label` and `note` ride along because the equity curve draws these as their
 * own kind of point and a hover has to be able to say WHICH adjustment — "+$500"
 * with no name is indistinguishable from an unusually good trade.
 */
export interface DashAdjustmentLeg {
  date: string;
  amount: number;
  label: string;
  note: string;
  accountId: string;
}

/**
 * In-scope, in-window, balance-moving adjustments — oldest first.
 *
 * ⚠ An EMPTY selection means every account, not no account. That is the default
 * state of the filter chip ("All accounts"), so getting it backwards would empty
 * the total for almost every user while looking perfectly reasonable in a test
 * that always selects something.
 */
export function dashAdjustmentLegs(
  accounts: readonly DashAdjustmentAccount[] | null | undefined,
  selection: readonly string[] | null | undefined,
  range: DashRange | null | undefined,
): DashAdjustmentLeg[] {
  if (!accounts?.length) return [];
  const ids = selection?.length ? new Set(selection) : null;
  const start = range?.startDate ?? null;
  const end = range?.endDate ?? null;

  const out: DashAdjustmentLeg[] = [];
  for (const a of accounts) {
    if (ids && !ids.has(a.id)) continue;
    for (const adj of a.adjustments ?? []) {
      if (!isBalanceMoving(adj)) continue;
      const date = adj.date || '';
      // ⚠ String comparison, and it is correct ONLY because these are ISO dates.
      // The same shortcut beta takes, and the reason `date` is stored as
      // `YYYY-MM-DD` everywhere rather than as a locale string.
      if (start && date < start) continue;
      if (end && date > end) continue;
      out.push({
        date,
        amount: Number(adj.amount) || 0,
        label: adj.type || '',
        note: adj.note || '',
        accountId: a.id,
      });
    }
  }
  return out.sort((x, y) => x.date.localeCompare(y.date));
}

/** What the headline adds to the trade total. */
export function dashAdjustmentTotal(
  legs: readonly DashAdjustmentLeg[] | null | undefined,
): number {
  return (legs ?? []).reduce((s, l) => s + l.amount, 0);
}
